// Onda 2 (2026-06-02): migrado pra Antd — Input.TextArea + Button. EventoItem mantido (ja Antd).
import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Input, Button, Space, Card, Spin, Empty, Segmented, App as AntApp,
} from 'antd';
import { SendOutlined } from '@ant-design/icons';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';
import type { Negocio } from './types';
import { EventoItem } from './EventoItem';
import { CANAIS_INTERACAO } from './utils';

export function AbaTimeline({ negocioId, negocio, reloadFlag }: { negocioId: number; negocio: Negocio; reloadFlag: number }) {
  const { message } = AntApp.useApp();
  const [eventos, setEventos] = useState<any[]>([]);
  const [atividades, setAtividades] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [novoTexto, setNovoTexto] = useState('');
  const [canal, setCanal] = useState('nota');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => { carregar(); }, [negocioId, reloadFlag]);
  async function carregar() {
    setLoading(true);
    try {
      const token = await getToken();
      const [resEv, resAt] = await Promise.all([
        axios.get(`${API_URL}/comercial/propostas/${negocioId}/eventos`,
          { headers: { Authorization: token } }),
        axios.get(`${API_URL}/atividades?escopo=origem&origem_modulo=comercial&origem_id=${negocioId}`,
          { headers: { Authorization: token } }).catch(() => null),
      ]);
      const dataEv = resEv.data?.body ? JSON.parse(resEv.data.body) : resEv.data;
      setEventos(Array.isArray(dataEv) ? dataEv : []);
      if (resAt) {
        const dataAt = resAt.data?.body ? JSON.parse(resAt.data.body) : resAt.data;
        setAtividades(Array.isArray(dataAt) ? dataAt : []);
      } else {
        setAtividades([]);
      }
    } finally { setLoading(false); }
  }
  async function registrar() {
    if (!novoTexto.trim()) return;
    setSalvando(true);
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/comercial/propostas/${negocioId}/eventos`,
        // O canal vai no `meta`; o tipo continua 'nota' (ver utils.ts).
        { tipo: 'nota', descricao: novoTexto.trim(), ...(canal !== 'nota' ? { meta: { canal } } : {}) },
        { headers: { Authorization: token } });
      setNovoTexto('');
      setCanal('nota');
      await carregar();
    } catch (e: any) { message.error('Erro: ' + e.message); }
    finally { setSalvando(false); }
  }
  async function excluir(id: string) {
    try {
      const token = await getToken();
      await axios.delete(`${API_URL}/comercial/eventos/${id}`, { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) { message.error('Erro: ' + e.message); }
  }
  async function concluirAtividade(id: string) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/atividades`,
        { action: 'update', id, status: 'concluida' },
        { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  }
  async function reabrirAtividade(id: string) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/atividades`,
        { action: 'update', id, status: 'pendente' },
        { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  }
  async function excluirAtividade(id: string) {
    try {
      const token = await getToken();
      await axios.post(`${API_URL}/atividades`,
        { action: 'delete', id },
        { headers: { Authorization: token } });
      await carregar();
    } catch (e: any) { message.error('Erro: ' + (e.response?.data?.error || e.message)); }
  }

  // Sintetiza evento "negócio criado" usando criado_em (TIMESTAMP) ou fallback data_abertura
  const eventoCriacao = {
    id: 'created-' + negocio.id,
    tipo: 'negocio_criado',
    descricao: 'Negócio criado',
    criado_em: negocio.criado_em || negocio.data_abertura,
    sintetico: true,
  };
  // Atividades viram "eventos sinteticos" na timeline.
  const eventosAtividade = atividades.map((a: any) => ({
    id: 'ativ-' + a.id,
    tipo: 'atividade',
    descricao: a.titulo,
    criado_em: a.data_conclusao || a.data_vencimento || a.criado_em,
    criado_por: a.criado_por,
    atividade: a,
  }));
  const lista = [...eventos, ...eventosAtividade, eventoCriacao]
    .sort((a: any, b: any) => new Date(b.criado_em).getTime() - new Date(a.criado_em).getTime());

  return (
    <div>
      <Card size="small" style={{ marginBottom: 12, background: '#FAFBFF' }}
        bodyStyle={{ padding: 10 }}>
        <Segmented size="small" value={canal} onChange={(v) => setCanal(String(v))}
          style={{ marginBottom: 8 }}
          options={CANAIS_INTERACAO.map(c => ({ value: c.chave, label: `${c.emoji} ${c.rotulo}` }))} />
        <Space.Compact style={{ width: '100%' }}>
          <Input.TextArea
            value={novoTexto} onChange={e => setNovoTexto(e.target.value)}
            placeholder={(CANAIS_INTERACAO.find(c => c.chave === canal) || CANAIS_INTERACAO[0]).dica}
            autoSize={{ minRows: 2, maxRows: 5 }}
            disabled={salvando}
          />
          <Button type="primary" icon={<SendOutlined />} onClick={registrar}
            disabled={salvando || !novoTexto.trim()} loading={salvando}>
            Registrar
          </Button>
        </Space.Compact>
      </Card>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : lista.length === 0 ? (
        <Empty description="Nenhum evento" />
      ) : (
        <div>
          {lista.map((ev: any) => (
            <EventoItem key={ev.id} evento={ev}
              onExcluir={ev.sintetico ? null : ev.tipo === 'atividade' ? null : () => excluir(ev.id)}
              onConcluirAtividade={concluirAtividade}
              onReabrirAtividade={reabrirAtividade}
              onExcluirAtividade={excluirAtividade} />
          ))}
        </div>
      )}
    </div>
  );
}
